// A FEJLESZTÉS-IDŐVONAL — a kiadás-történet, legfrissebb elöl, HÓNAP-csoportokkal.
//
// MIÉRT EZ AZ ALAK: a változásnapló bevett formája a fordított időrend, és a görgetés közben az idő-csoport
// fejléce az, ami elhelyez („hol tartok?"). Ezért a fejléc RAGADÓS: a hosszú listán végig látszik, melyik
// hónapot olvasod.
//
// A DÁTUM NÉLKÜLI tétel nem vész el: a lista végén, saját csoportban áll, kimondva, hogy nincs időpontja.
// Némán eldobni rosszabb lenne — a felhasználó nem tudná, hogy hiányzik valami.
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ProductUpdateCard,
  type ProductUpdateCardItem,
} from '@/components/product-updates/product-update-card';
import { Text } from '@/components/ui/text';
import { toTimelineGroups } from '@/features/product-updates/views';
import { DEFAULT_TIME_ZONE, formatDateTime } from '@/lib/format-datetime';

export interface TimelineRow extends ProductUpdateCardItem {
  shippedAt?: string | null;
  publishedAt?: string | null;
}

export interface ProductUpdateTimelineProps<T extends TimelineRow> {
  rows: readonly T[];
  title: (row: T) => string;
  meta?: ((row: T) => ReactNode) | undefined;
  onOpen?: ((row: T) => void) | undefined;
  actions?: ((row: T) => ReactNode) | undefined;
  /** Végtelen görgetés: van-e még lap, és mi tölti be. Hiányukban a lista egyszerűen véges. */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: (() => void) | undefined;
}

/** `YYYY-MM` → lokalizált hónap-felirat. A DÉLI időpont a nap-eltolódás ellen véd. */
function monthLabel(month: string, language: string): string {
  return formatDateTime(`${month}-15T12:00:00.000Z`, language, DEFAULT_TIME_ZONE, {
    year: 'numeric',
    month: 'long',
  });
}

export function ProductUpdateTimeline<T extends TimelineRow>({
  rows,
  title,
  meta,
  onOpen,
  actions,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
}: ProductUpdateTimelineProps<T>): JSX.Element {
  const { t, i18n } = useTranslation();
  const groups = toTimelineGroups(rows);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // VÉGTELEN GÖRGETÉS — a `DataTable` mintája: a sentinel a BELSŐ görgetőn belül van, ezért az observer
  // gyökere a görgető div, nem a viewport. A friss callbacket ref adja, hogy az observer ne épüljön újra
  // minden renderen.
  const latest = useRef({ onLoadMore, isFetchingNextPage });
  latest.current = { onLoadMore, isFetchingNextPage };
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !latest.current.isFetchingNextPage)
          latest.current.onLoadMore?.();
      },
      { root: scrollRef.current },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage]);

  return (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
      <div className="space-y-6 pb-4">
        {groups.map((group) => (
          <section key={group.month || 'no-date'} aria-label={group.month || undefined}>
            {/* A RAGADÓS csoport-fejléc: ez a „csík" a különböző időszakok között. A vonal a fejléc alatt
                fut végig, hogy a csoport-határ akkor is látszódjon, ha a fejléc épp a lista tetején ragad. */}
            <div className="bg-background/95 sticky top-0 z-10 flex items-center gap-3 py-1.5 backdrop-blur">
              <Text variant="overline" as="h3" className="shrink-0">
                {group.month === ''
                  ? t('productUpdates.timeline.noDate')
                  : monthLabel(group.month, i18n.language)}
              </Text>
              <span className="bg-border h-px flex-1" aria-hidden />
              <Text tone="tertiary" as="span" className="shrink-0 text-xs">
                {t('productUpdates.timeline.count', { count: group.items.length })}
              </Text>
            </div>
            <div className="mt-2 space-y-2">
              {group.items.map((row) => (
                <ProductUpdateCard
                  key={row.id}
                  item={row}
                  title={title(row)}
                  meta={meta?.(row)}
                  onOpen={onOpen === undefined ? undefined : () => onOpen(row)}
                  actions={actions?.(row)}
                />
              ))}
            </div>
          </section>
        ))}
        {hasNextPage && <div ref={sentinelRef} aria-hidden className="h-8" />}
      </div>
    </div>
  );
}
